/**
 * ai-agent-chip.tsx — Thẻ nhỏ trên dòng hội thoại ở bộ lọc "Nhân viên AI": nhân viên AI nào đang phục vụ + trạng thái
 * LÚC NÀY (backend chat/ai-inbox-view.ts → `conversation.aiAgent`). Hội thoại không có tên nhân viên AI (server cũ:
 * "AI chung" của tổ chức) → hiện tên nhân viên AI mặc định (`isDefault`) nếu có, không thì "AI chung".
 */
import { Bot } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { useAiBots } from '@/hooks/use-ai'
import { defaultAiName } from '@/lib/ai-khung'
import type { AiAgentInfo, AiAgentState } from '@/hooks/use-conversations'

/** Nhãn trạng thái — getter đọc i18n lúc dùng, luôn theo ngôn ngữ đang chọn. */
export const AI_STATE_LABEL: Record<AiAgentState, string> = {
  get auto() { return i18n.t('convChat:aiChip.auto') },
  get suggest() { return i18n.t('convChat:aiChip.suggest') },
  get manual() { return i18n.t('convChat:aiChip.manual') },
  get paused() { return i18n.t('convChat:aiChip.paused') },
  get offline() { return i18n.t('convChat:aiChip.offline') },
  get off() { return i18n.t('convChat:aiChip.off') },
}

const STATE_CLASS: Record<AiAgentState, string> = {
  auto: 'bg-primary/10 text-primary',
  suggest: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  manual: 'bg-muted text-muted-foreground',
  paused: 'bg-muted text-muted-foreground',
  offline: 'bg-muted text-muted-foreground',
  off: 'bg-muted text-muted-foreground',
}

/** "AI Lan · Tự động"; không có tên → tên nhân viên AI mặc định (`defaultName`), dự phòng "AI chung". */
export function aiAgentText(a: AiAgentInfo, defaultName?: string | null): string {
  const name = a.botName ? i18n.t('convChat:aiChip.botName', { name: a.botName }) : defaultName?.trim() || i18n.t('convChat:aiChip.general')
  return i18n.t('convChat:aiChip.text', { name, state: AI_STATE_LABEL[a.state] })
}

export function AiAgentChip({ agent, className }: { agent: AiAgentInfo; className?: string }) {
  useTranslation('convChat') // vẽ lại khi đổi ngôn ngữ
  const { data } = useAiBots()
  const text = aiAgentText(agent, agent.botName ? null : defaultAiName(data?.bots))
  return (
    <span
      className={cn(
        'inline-flex max-w-[150px] shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium',
        STATE_CLASS[agent.state],
        className,
      )}
      title={text}
    >
      <Bot className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">{text}</span>
    </span>
  )
}
